'use client';

import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useEffect, useRef, useState } from 'react';
import { Breadcrumbs } from '../breadcrumbs';
import { HtmlDescription } from '../../ui/html-description';
import { GoArrowDown, GoArrowUp } from 'react-icons/go';

export function ProductHeader({ product }: { product: ProductDetailData }) {
  const { t } = useTranslations();

  const descriptionRef = useRef<HTMLDivElement>(null);

  const [isClamped, setIsClamped] = useState(true);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const element = descriptionRef.current;
    if (element) {
      const isOverflow = element.scrollHeight > element.clientHeight + 1;
      setIsOverflowing(isOverflow);
    }
  }, [product.short_description]);

  const handleToggleClamp = () => {
    setIsClamped(prev => !prev);
  };

  return (
    <div className="flex w-full flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6">
      <Breadcrumbs product={product} />
      <div className="flex w-full flex-col gap-2">
        <h1 data-ea-primary-heading className="text-3xl font-medium tracking-tight sm:text-4xl">
          {product.name}
        </h1>
        <p className="text-sm font-light text-black/70">
          {t('text228')}&nbsp;<span className="font-semibold text-black/100">{product.code}</span>
        </p>
      </div>
      <div
        ref={descriptionRef}
        className={cn(
          'w-full transition-all duration-300',
          'ea-product-header__short-description-container',
          {
            'line-clamp-3': isClamped,
          },
        )}
      >
        <HtmlDescription
          sectionContent
          ref={descriptionRef}
          color="foreground"
          content={product.short_description}
          size="sm"
          className={cn('w-full', 'ea-product-header__short-description')}
        />
      </div>
      {isOverflowing && (
        <div
          onClick={handleToggleClamp}
          className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm tracking-tight text-primary transition-all hover:opacity-90 sm:text-base"
        >
          <Button
            variant="alternative-default"
            size="icon"
            aria-label={isClamped ? t('text1360') : t('text604')}
            aria-expanded={!isClamped}
          >
            {isClamped ? (
              <GoArrowDown size={24} className="text-primary" />
            ) : (
              <GoArrowUp size={24} className="text-primary" />
            )}
          </Button>
          {isClamped ? t('text1360') : t('text604')}
        </div>
      )}
    </div>
  );
}
